/**
 * AdminLegalContent.jsx — Yasal Belge (KVKK/Gizlilik/Şartlar) İçerik Yönetimi
 *
 * DİKKAT: AdminLegal.jsx ile karıştırılmamalı — o sayfa icra/avukat dosyaları
 * için ayrı bir alandır. Bu sayfa yalnızca yasal metin editörü + publish akışı.
 *
 * doc_type: kvkk | privacy | terms
 * lang: tr | en
 *
 * Backend uçları (require_admin):
 *   GET    /admin/legal/documents                      → tüm taslak+yayın belgeler
 *   GET    /admin/legal/documents/{doc_type}?lang=      → tekil belge (404 olabilir)
 *   PUT    /admin/legal/documents/{doc_type}?lang=      → upsert (taslak, publish durumu değişmez)
 *   POST   /admin/legal/documents/{doc_type}/publish?lang=
 *   POST   /admin/legal/documents/{doc_type}/unpublish?lang=
 *
 * Tasarım: design_guidelines.json — Vibrant Play + Neumorphism Clay
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { sanitizeHtmlPreview, renderMarkdownPreview } from '../utils/legalContentRender';
import {
  FileText,
  ShieldCheck,
  Scroll,
  Eye,
  Save,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Calendar,
  Clock,
  User,
  Hash,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { id: 'kvkk', icon: ShieldCheck },
  { id: 'privacy', icon: FileText },
  { id: 'terms', icon: Scroll },
];

const LANGS = ['tr', 'en'];

const EMPTY_DOC = {
  title: '',
  content: '',
  format: 'markdown',
  effective_date: '',
  is_published: false,
  version: 0,
  updated_at: null,
  updated_by: null,
};

// ── Markdown/HTML önizleme render'ı — bkz. utils/legalContentRender.js ──────
// (sanitizeHtmlPreview, renderMarkdownPreview yukarıdan import edilir; PUBLIC
// /legal/:docType sayfası ile TEK sanitize noktasını paylaşır.)

// ── Loading / Empty / Error ───────────────────────────────────────────────────
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 size={32} className="animate-spin text-[var(--brand-primary)]" />
    </div>
  );
}

function ErrorState({ msg, onRetry, t }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
      <AlertTriangle size={32} className="text-rose-500" />
      <p className="font-bold text-rose-600">{msg}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 rounded-xl bg-rose-500 text-white text-sm font-bold hover:bg-rose-600 transition-colors"
        >
          {t('common.retry') || 'Tekrar Dene'}
        </button>
      )}
    </div>
  );
}

function fmtDateTime(val, lang) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US');
  } catch {
    return val;
  }
}

// ── Durum rozeti ───────────────────────────────────────────────────────────
function StatusBadge({ doc, t }) {
  if (!doc || (!doc.title && !doc.content && !doc.updated_at)) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-bold border bg-slate-100 text-slate-500 border-slate-200">
        {t('admin.legalContent.status.empty')}
      </span>
    );
  }
  return doc.is_published ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-bold border bg-emerald-100 text-emerald-700 border-emerald-200">
      <CheckCircle2 size={12} />
      {t('admin.legalContent.status.published')} · v{doc.version || 1}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-bold border bg-amber-100 text-amber-700 border-amber-200">
      <Clock size={12} />
      {t('admin.legalContent.status.draft')}
    </span>
  );
}

// ── Matris seçici (doc_type × lang) ──────────────────────────────────────────
function DocMatrix({ docMap, selected, onSelect, t }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-[var(--border)] mb-6">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg-muted)]">
          <tr>
            <th className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">
              {t('admin.legalContent.matrix.docType')}
            </th>
            {LANGS.map((lang) => (
              <th key={lang} className="px-4 py-3 text-left font-bold text-[var(--text-muted)]">
                {t(`admin.legalContent.lang.${lang}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border)]">
          {DOC_TYPES.map(({ id, icon: Icon }) => (
            <tr key={id} className="bg-[var(--bg-card)]">
              <td className="px-4 py-3 font-semibold text-[var(--text-main)]">
                <div className="flex items-center gap-2">
                  <Icon size={16} className="text-[var(--brand-primary)]" />
                  {t(`admin.legalContent.docType.${id}`)}
                </div>
              </td>
              {LANGS.map((lang) => {
                const key = `${id}_${lang}`;
                const doc = docMap[key];
                const isSelected = selected.docType === id && selected.lang === lang;
                return (
                  <td key={lang} className="px-4 py-3">
                    <button
                      onClick={() => onSelect(id, lang)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-bold transition-all ${
                        isSelected
                          ? 'bg-[var(--brand-primary)] text-white border-transparent'
                          : 'bg-[var(--bg-muted)] text-[var(--text-main)] border-[var(--border)] hover:bg-[var(--bg-card)]'
                      }`}
                    >
                      {isSelected ? (
                        <span className="text-white">{doc && doc.is_published ? t('admin.legalContent.status.published') : t('admin.legalContent.status.draft')}</span>
                      ) : (
                        <StatusBadge doc={doc} t={t} />
                      )}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Editör paneli ─────────────────────────────────────────────────────────
function DocEditor({ docType, lang, doc, onSaved, t, uiLang, authHeaders }) {
  const toast = useToast();
  const [form, setForm] = useState(doc);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    setForm(doc);
    setShowPreview(false);
  }, [doc, docType, lang]);

  const previewHtml = useMemo(() => {
    if (form.format === 'html') return sanitizeHtmlPreview(form.content);
    return renderMarkdownPreview(form.content);
  }, [form.content, form.format]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/legal/documents/${docType}?lang=${lang}`,
        {
          title: form.title,
          content: form.content,
          format: form.format,
          effective_date: form.effective_date || null,
        },
        { headers: authHeaders() }
      );
      toast.success(t('admin.legalContent.msg.saved'));
      onSaved(res.data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, uiLang));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const res = await axios.post(
        `${API}/admin/legal/documents/${docType}/publish?lang=${lang}`,
        {},
        { headers: authHeaders() }
      );
      toast.success(t('admin.legalContent.msg.published'));
      onSaved(res.data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, uiLang));
    } finally {
      setPublishing(false);
    }
  };

  const unpublish = async () => {
    setPublishing(true);
    try {
      const res = await axios.post(
        `${API}/admin/legal/documents/${docType}/unpublish?lang=${lang}`,
        {},
        { headers: authHeaders() }
      );
      toast.success(t('admin.legalContent.msg.unpublished'));
      onSaved(res.data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, uiLang));
    } finally {
      setPublishing(false);
    }
  };

  const hasContent = !!(form.content && form.content.trim());

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-[var(--text-main)]">
            {t(`admin.legalContent.docType.${docType}`)} · {t(`admin.legalContent.lang.${lang}`)}
          </h3>
          <div className="flex items-center gap-3 mt-1 text-xs text-[var(--text-muted)]">
            <StatusBadge doc={doc} t={t} />
            {doc.updated_at && (
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {fmtDateTime(doc.updated_at, uiLang)}
              </span>
            )}
            {doc.updated_by && (
              <span className="flex items-center gap-1">
                <User size={12} />
                {doc.updated_by}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPreview((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[var(--border)] text-xs font-bold text-[var(--text-main)] hover:bg-[var(--bg-muted)] transition-colors"
          >
            <Eye size={14} />
            {showPreview ? t('admin.legalContent.btn.hidePreview') : t('admin.legalContent.btn.preview')}
          </button>
          {doc.is_published ? (
            <button
              onClick={unpublish}
              disabled={publishing}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500 text-white text-xs font-bold disabled:opacity-40 hover:bg-amber-600 transition-colors"
            >
              {publishing ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
              {t('admin.legalContent.btn.unpublish')}
            </button>
          ) : (
            <button
              onClick={publish}
              disabled={publishing || !hasContent}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold disabled:opacity-40 hover:bg-emerald-600 transition-colors"
              title={!hasContent ? t('admin.legalContent.hint.needContent') : undefined}
            >
              {publishing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {t('admin.legalContent.btn.publish')}
            </button>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold disabled:opacity-40 hover:opacity-90 transition-opacity"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {t('admin.legalContent.btn.save')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label htmlFor="lc-title" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
            {t('admin.legalContent.field.title')}
          </label>
          <input
            id="lc-title"
            value={form.title || ''}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder={t('admin.legalContent.field.titlePlaceholder')}
            className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
          />
        </div>
        <div>
          <label htmlFor="lc-effdate" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
            <Calendar size={12} className="inline mr-1" />
            {t('admin.legalContent.field.effectiveDate')}
          </label>
          <input
            id="lc-effdate"
            type="date"
            value={form.effective_date || ''}
            onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
          {t('admin.legalContent.field.format')}
        </label>
        <div className="flex gap-2">
          {['markdown', 'html'].map((fmt) => (
            <button
              key={fmt}
              onClick={() => setForm((f) => ({ ...f, format: fmt }))}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-colors ${
                form.format === fmt
                  ? 'bg-[var(--brand-primary)] text-white border-transparent'
                  : 'bg-[var(--bg-muted)] text-[var(--text-main)] border-[var(--border)] hover:bg-[var(--bg-card)]'
              }`}
            >
              {t(`admin.legalContent.format.${fmt}`)}
            </button>
          ))}
        </div>
      </div>

      <div className={showPreview ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : ''}>
        <div>
          <label htmlFor="lc-content" className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
            {t('admin.legalContent.field.content')}
          </label>
          <textarea
            id="lc-content"
            value={form.content || ''}
            onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
            rows={16}
            placeholder={t('admin.legalContent.field.contentPlaceholder')}
            className="w-full px-3 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
          />
        </div>
        {showPreview && (
          <div>
            <p className="text-xs font-semibold text-[var(--text-muted)] block mb-1">
              {t('admin.legalContent.preview.title')}
            </p>
            <div className="w-full h-full min-h-[200px] px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--bg-muted)] text-sm overflow-y-auto max-h-[420px]">
              {hasContent ? (
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              ) : (
                <p className="text-[var(--text-muted)] italic">{t('admin.legalContent.preview.empty')}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ANA SAYFA
// ─────────────────────────────────────────────────────────────────────────────

export default function AdminLegalContent() {
  const { lang, t } = useLanguage();
  const { authHeaders, user } = useAuth();
  const tr = (trText, enText) => (lang === 'tr' ? trText : enText);
  const [docMap, setDocMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState({ docType: 'kvkk', lang: 'tr' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/legal/documents`, { headers: authHeaders() });
      const map = {};
      (res.data.documents || []).forEach((d) => {
        map[`${d.doc_type}_${d.lang}`] = d;
      });
      setDocMap(map);
    } catch (err) {
      setError(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, lang]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const selectedKey = `${selected.docType}_${selected.lang}`;
  const selectedDoc = { ...EMPTY_DOC, ...(docMap[selectedKey] || {}) };

  const handleSaved = (updatedDoc) => {
    setDocMap((prev) => ({
      ...prev,
      [`${updatedDoc.doc_type || selected.docType}_${updatedDoc.lang || selected.lang}`]: {
        ...prev[selectedKey],
        ...updatedDoc,
        doc_type: updatedDoc.doc_type || selected.docType,
        lang: updatedDoc.lang || selected.lang,
      },
    }));
  };

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          rootBreadcrumb(user?.role, tr),
          { label: t('admin.nav.legalContent') },
        ]}
        title={t('admin.nav.legalContent')}
        subtitle={t('admin.legalContent.subtitle')}
      />

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState msg={error} onRetry={fetchAll} t={t} />
      ) : (
        <>
          <DocMatrix
            docMap={docMap}
            selected={selected}
            onSelect={(docType, lng) => setSelected({ docType, lang: lng })}
            t={t}
          />

          <AnimatePresence mode="wait">
            <motion.div
              key={selectedKey}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
            >
              <DocEditor
                docType={selected.docType}
                lang={selected.lang}
                doc={selectedDoc}
                onSaved={handleSaved}
                t={t}
                uiLang={lang}
                authHeaders={authHeaders}
              />
            </motion.div>
          </AnimatePresence>

          <p className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] mt-4">
            <Hash size={12} />
            {t('admin.legalContent.hint.contract')}
          </p>
        </>
      )}
    </AdminLayout>
  );
}
